import { useCallback, useEffect, useState } from 'react';
import { Check, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingState } from '@/components/ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermission } from '@/components/rbac/use-permission';
import { useStandort } from '@/components/layouts/use-standort';
import { P } from '@/lib/rbac/permissions.generated';
import { api } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/dateFormat';

type Lehrerwechsel = {
  id: number;
  status: string;
  abgelaufen: boolean;
  kursId: number;
  kursName: string | null;
  bisherigeLehrkraft: string | null;
  neueLehrkraft: string | null;
  slot: { date?: string; dayOfWeek?: number; startTime?: string; endTime?: string } | null;
  anchorStartDate: string | null;
  teilnehmende: number;
  expiresAt: string | null;
  createdAt: string | null;
};

const STATUS: Record<string, { label: string; farbe: string }> = {
  angelegt: { label: 'Angelegt', farbe: 'border-slate-300 text-slate-600' },
  versendet: { label: 'Angebot versendet', farbe: 'border-amber-300 text-amber-800' },
  angenommen: { label: 'Angenommen – bitte bestätigen', farbe: 'border-emerald-300 text-emerald-700' },
  abgelehnt: { label: 'Von der Lehrkraft abgelehnt', farbe: 'border-red-300 text-red-700' },
  'bestätigt': { label: 'Bestätigt', farbe: 'border-slate-300 text-slate-500' },
};

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function termin(eintrag: Lehrerwechsel): string {
  const slot = eintrag.slot;
  if (!slot) return '—';
  const tag = typeof slot.dayOfWeek === 'number' ? WOCHENTAG[slot.dayOfWeek % 7] : '';
  const zeit = [slot.startTime, slot.endTime].filter(Boolean).join('–');
  return [tag, zeit].filter(Boolean).join(' ') || '—';
}

/**
 * Lehrerwechsel im Überblick (T-0089).
 *
 * Ein Auftrag war nur sichtbar, solange der Assistent offen war. Hier stehen alle
 * noch nicht bestätigten — mit den Schritten, die dann fehlen: bestätigen, sobald
 * die Lehrkraft angenommen hat, oder das Angebot erneut senden, wenn es abgelaufen ist.
 */
export default function LehrerwechselPage() {
  const { selectedStandortId } = useStandort();
  const darfBearbeiten = usePermission(P.termine.eintraege_bearbeiten);
  const [eintraege, setEintraege] = useState<Lehrerwechsel[]>([]);
  const [status, setStatus] = useState('offen');
  const [laedt, setLaedt] = useState(true);
  const [arbeitetAn, setArbeitetAn] = useState<number | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const params = new URLSearchParams({ status });
      if (selectedStandortId) params.set('standortId', String(selectedStandortId));
      const antwort = await api.get<{ items: Lehrerwechsel[] }>(`/api/teacher-change-orders?${params.toString()}`);
      setEintraege(Array.isArray(antwort?.items) ? antwort.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Lehrerwechsel konnten nicht geladen werden');
      setEintraege([]);
    } finally {
      setLaedt(false);
    }
  }, [status, selectedStandortId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const aktion = async (eintrag: Lehrerwechsel, art: 'confirm' | 'send-offer') => {
    setArbeitetAn(eintrag.id);
    try {
      await api.post(`/api/teacher-change-orders/${eintrag.id}/${art}`, {});
      toast.success(art === 'confirm' ? 'Lehrerwechsel bestätigt' : 'Angebot erneut gesendet');
      await laden();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Das hat nicht geklappt');
    } finally {
      setArbeitetAn(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Lehrerwechsel</h1>
          <p className="text-muted-foreground">
            Aufträge an eine neue Lehrkraft. Neue Wechsel startest Du wie bisher im Stundenplan über den Kurs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" aria-label="Status filtern">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="offen">Offen</SelectItem>
              <SelectItem value="bestätigt">Bestätigt</SelectItem>
              <SelectItem value="alle">Alle</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => void laden()} aria-label="Neu laden">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {laedt ? (
        <LoadingState message="Lehrerwechsel werden geladen …" />
      ) : eintraege.length === 0 ? (
        <EmptyState message={status === 'offen' ? 'Keine offenen Lehrerwechsel.' : 'Keine Lehrerwechsel in dieser Ansicht.'} />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kurs</TableHead>
                <TableHead>Wechsel</TableHead>
                <TableHead>Neuer Termin</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Angelegt</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eintraege.map((eintrag) => {
                const info = STATUS[eintrag.status] ?? { label: eintrag.status, farbe: '' };
                const beschaeftigt = arbeitetAn === eintrag.id;
                const erneutSenden = eintrag.abgelaufen || eintrag.status === 'angelegt';
                return (
                  <TableRow key={eintrag.id}>
                    <TableCell>
                      <div className="font-medium">{eintrag.kursName || `Kurs #${eintrag.kursId}`}</div>
                      <div className="text-xs text-muted-foreground">
                        {eintrag.teilnehmende} Teilnehmende
                        {eintrag.anchorStartDate ? ` · ab ${formatDate(eintrag.anchorStartDate)}` : ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      {eintrag.bisherigeLehrkraft || '—'} → <span className="font-medium">{eintrag.neueLehrkraft || '—'}</span>
                    </TableCell>
                    <TableCell>{termin(eintrag)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={info.farbe}>
                        {eintrag.abgelaufen ? 'Angebot abgelaufen' : info.label}
                      </Badge>
                      {eintrag.status === 'versendet' && !eintrag.abgelaufen && eintrag.expiresAt ? (
                        <div className="mt-1 text-xs text-muted-foreground">gültig bis {formatDate(eintrag.expiresAt)}</div>
                      ) : null}
                    </TableCell>
                    <TableCell>{eintrag.createdAt ? formatDateTime(eintrag.createdAt) : '—'}</TableCell>
                    <TableCell className="text-right">
                      {darfBearbeiten && eintrag.status === 'angenommen' ? (
                        <Button size="sm" disabled={beschaeftigt} onClick={() => void aktion(eintrag, 'confirm')}>
                          <Check className="mr-1 h-4 w-4" /> Bestätigen
                        </Button>
                      ) : null}
                      {darfBearbeiten && erneutSenden ? (
                        <Button variant="ghost" size="sm" disabled={beschaeftigt} onClick={() => void aktion(eintrag, 'send-offer')}>
                          <Send className="mr-1 h-4 w-4" /> Angebot senden
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
